import React, { useEffect, useState } from "react";
import {
  Typography, Paper, List, ListItem, ListItemText, IconButton, Button, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { fetchAllCustomers, createCustomer, deleteCustomer } from "../../api/api";
import { useTranslation } from "react-i18next";

interface CustomerManagementProps {
  onCustomerChange: () => void;
}

const CustomerManagement: React.FC<CustomerManagementProps> = ({ onCustomerChange }) => {
  const { t } = useTranslation();

  const [customers, setCustomers] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<string | null>(null);

  const reload = () => {
    fetchAllCustomers().then(setCustomers);
  };

  useEffect(() => {
    reload();
  }, []);

  const handleCreate = async () => {
    try { // TRY-BLOCK HINZUGEFÜGT
      await createCustomer(name);
      setDialogOpen(false);
      setName("");
      setSnackbar(t("customerManagement.customerCreated"));
      reload();
      onCustomerChange();
    } catch (error: any) { // CATCH-BLOCK HINZUGEFÜGT
      if (error && error.error === "duplicate_name") {
        setSnackbar(t("customerManagement.duplicateNameError")); // Neue i18n-Key
      } else {
        setSnackbar(t("common.createCustomerError")); // Generischer Fehler (optional)
        console.error("Error creating customer:", error);
      }
    }
  };

  const handleDeleteClick = (id: string) => {
    setCustomerToDelete(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (customerToDelete) {
      try { // TRY-BLOCK HINZUGEFÜGT
        await deleteCustomer(customerToDelete);
        setSnackbar(t("customerManagement.customerDeleted"));
        reload();
        onCustomerChange();
      } catch (error: any) { // CATCH-BLOCK HINZUGEFÜGT
        setSnackbar(t("common.deleteCustomerError")); // Generischer Fehler (optional)
        console.error("Error deleting customer:", error);
      }
    }
    setConfirmDialogOpen(false);
    setCustomerToDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setCustomerToDelete(null);
  };

  return (
    <Paper sx={{ p: 1.5, mt: 1.5, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{t("customerManagement.customersHeading")}</Typography>
        <Button variant="outlined" onClick={() => setDialogOpen(true)}>
          {t("customerManagement.createCustomerButton")}
        </Button>
      </Box>
      <List>
        {customers.map((customer) => (
          <ListItem
            key={customer.id}
            secondaryAction={
              <IconButton onClick={() => handleDeleteClick(customer.id)} color="error">
                <DeleteIcon />
              </IconButton>
            }
          >
            <ListItemText
              primary={customer.name}
            />
          </ListItem>
        ))}
        {customers.length === 0 && (
          <ListItem>
            <ListItemText primary={t("customerManagement.noCustomers")} />
          </ListItem>
        )}
      </List>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{t("customerManagement.createCustomerDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("customerManagement.customerNameLabel")}
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreate} disabled={!name}>{t("common.create")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">{t("customerManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography id="confirm-dialog-description">
            {t("customerManagement.confirmDeleteMessage", { customerName: customers.find(c => c.id === customerToDelete)?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={1800}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Paper>
  );
};

export default CustomerManagement;